import React, { useState, useMemo, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  BarChart3, 
  Users, 
  Mail, 
  Send, 
  Inbox, 
  Calendar, 
  DollarSign, 
  HardDrive, 
  Search, 
  Download, 
  TrendingUp, 
  Clock, 
  CheckCircle2, 
  Filter, 
  Sparkles,
  ArrowUpDown,
  Building2,
  FileCheck,
  ShieldCheck,
  FileSpreadsheet
} from 'lucide-react';

const UsageAnalytics = () => {
  const { users, emails, approvals, leaveHistory } = useContext(AppContext);
  
  const [searchTerm, setSearchTerm] = useState('');
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(() => now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => now.getMonth() + 1); // Default: Current month (해당월)
  const [periodMode, setPeriodMode] = useState('CURRENT'); // 'CURRENT' (당월) | 'SELECT' (지정월) | 'ALL' (전체기간)

  const activeEmployees = useMemo(() => {
    return (users || []).filter(u => u.UserID !== 'system-config' && u.Status !== 'INACTIVE');
  }, [users]);

  const departments = useMemo(() => {
    return Array.from(new Set((users || []).map(u => u.Department).filter(Boolean)));
  }, [users]);

  const [selectedDept, setSelectedDept] = useState('ALL');
  const [sortBy, setSortBy] = useState('activity');
  const [sortOrder, setSortOrder] = useState('desc');

  // Compute per-employee usage statistics
  const employeeStats = useMemo(() => {
    const targetYear = Number(selectedYear);
    const targetMonth = Number(selectedMonth);

    return activeEmployees.map(emp => {
      const empEmail = (emp.Email || '').toLowerCase();
      const empAliases = (emp.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
      const allAddresses = [empEmail, ...empAliases];

      // Helper date match
      const isInSelectedPeriod = (dateStr) => {
        if (periodMode === 'ALL') return true;
        if (!dateStr) return true;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return true;
        return d.getFullYear() === targetYear && (d.getMonth() + 1) === targetMonth;
      };

      // 1. Mail metrics for period
      const userEmails = (emails || []).filter(e => isInSelectedPeriod(e.Timestamp || e.DeliveredAt || e.CreatedAt));

      const sentMails = userEmails.filter(e => {
        const sender = (e.Sender || '').toLowerCase();
        return allAddresses.some(addr => sender.includes(addr));
      });

      const receivedMails = userEmails.filter(e => {
        const rec = (e.Recipient || '').toLowerCase();
        const cc = (e.Cc || '').toLowerCase();
        return allAddresses.some(addr => rec.includes(addr) || cc.includes(addr));
      });

      const totalMails = sentMails.length + receivedMails.length;

      // 2. Approvals & Leaves metrics for period
      const userApprovals = (approvals || []).filter(a => {
        if (a.ApplicantID !== emp.UserID) return false;
        return isInSelectedPeriod(a.CreatedAt || a.StartDate || a.ExpenseDate);
      });
      const leaveApprovals = userApprovals.filter(a => a.Type === 'LEAVE');
      const expenseApprovals = userApprovals.filter(a => a.Type === 'EXPENSE');

      const totalExpenseAmount = expenseApprovals.reduce((sum, a) => {
        const amt = Number(a.Amount || 0);
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);

      const usedLeaveDays = Number(emp.UsedLeave || 0);
      const remainingLeaveDays = Number(emp.RemainingLeave || 15);
      const totalLeaveDays = usedLeaveDays + remainingLeaveDays;
      const leaveUsagePercent = totalLeaveDays > 0 ? Math.round((usedLeaveDays / totalLeaveDays) * 100) : 0;

      // 3. Storage metrics
      const storageUsageMb = Number(emp.StorageUsageMB || 0);
      const storageQuotaGb = Number(emp.StorageQuotaGB || 15);
      const storageQuotaMb = storageQuotaGb * 1024;
      const storageUsagePercent = storageQuotaMb > 0 ? Math.min(Math.round((storageUsageMb / storageQuotaMb) * 100), 100) : 0;

      // 4. Activity score calculation for ranking
      const activityScore = (sentMails.length * 3) + (receivedMails.length * 1) + (userApprovals.length * 4);

      // 5. Recent activity timestamp
      let latestTimestamp = emp.CreatedAt || '2026-08-01T09:00:00Z';
      sentMails.forEach(m => {
        if (m.Timestamp && m.Timestamp > latestTimestamp) latestTimestamp = m.Timestamp;
      });
      userApprovals.forEach(a => {
        if (a.Timestamp && a.Timestamp > latestTimestamp) latestTimestamp = a.Timestamp;
      });

      return {
        ...emp,
        sentMailsCount: sentMails.length,
        receivedMailsCount: receivedMails.length,
        totalMailsCount: totalMails,
        leaveApprovalsCount: leaveApprovals.length,
        expenseApprovalsCount: expenseApprovals.length,
        totalExpenseAmount,
        usedLeaveDays,
        remainingLeaveDays,
        leaveUsagePercent,
        storageUsageMb,
        storageQuotaGb,
        storageUsagePercent,
        activityScore,
        latestActivity: latestTimestamp
      };
    });
  }, [activeEmployees, emails, approvals, selectedYear, selectedMonth, periodMode]);

  // Filter and sort employee stats
  const filteredAndSortedStats = useMemo(() => {
    return employeeStats
      .filter(emp => {
        if (selectedDept !== 'ALL' && emp.Department !== selectedDept) return false;
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          const matchName = (emp.Name || '').toLowerCase().includes(term);
          const matchEmail = (emp.Email || '').toLowerCase().includes(term);
          const matchDept = (emp.Department || '').toLowerCase().includes(term);
          const matchRole = (emp.Position || '').toLowerCase().includes(term);
          return matchName || matchEmail || matchDept || matchRole;
        }
        return true;
      })
      .sort((a, b) => {
        let valA, valB;
        switch (sortBy) {
          case 'mailSent':
            valA = a.sentMailsCount;
            valB = b.sentMailsCount;
            break;
          case 'leaveUsed':
            valA = a.usedLeaveDays;
            valB = b.usedLeaveDays;
            break;
          case 'expenseSum':
            valA = a.totalExpenseAmount;
            valB = b.totalExpenseAmount;
            break;
          case 'storage':
            valA = a.storageUsageMb;
            valB = b.storageUsageMb;
            break;
          case 'name':
            valA = a.Name;
            valB = b.Name;
            return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          case 'activity':
          default:
            valA = a.activityScore;
            valB = b.activityScore;
            break;
        }
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      });
  }, [employeeStats, selectedDept, searchTerm, sortBy, sortOrder]);

  // Overall KPI aggregations
  const overallKpis = useMemo(() => {
    const totalUsers = activeEmployees.length;
    const totalMails = (emails || []).length;
    const totalSentMails = employeeStats.reduce((sum, e) => sum + e.sentMailsCount, 0);
    const totalExpense = employeeStats.reduce((sum, e) => sum + e.totalExpenseAmount, 0);
    const totalStorageMb = employeeStats.reduce((sum, e) => sum + e.storageUsageMb, 0);
    const totalStorageGb = (totalStorageMb / 1024).toFixed(1);
    const totalApprovalsCount = (approvals || []).length;
    const totalUsedLeaves = employeeStats.reduce((sum, e) => sum + e.usedLeaveDays, 0);

    return {
      totalUsers,
      totalMails,
      totalSentMails,
      totalExpense,
      totalStorageGb,
      totalApprovalsCount,
      totalUsedLeaves
    };
  }, [activeEmployees, emails, employeeStats, approvals]);

  // Department level summary
  const deptSummary = useMemo(() => {
    const map = {};
    departments.forEach(d => {
      map[d] = {
        name: d,
        userCount: 0,
        sentMails: 0,
        expenseSum: 0,
        leaveUsed: 0
      };
    });

    employeeStats.forEach(emp => {
      const d = emp.Department || '미지정';
      if (!map[d]) {
        map[d] = { name: d, userCount: 0, sentMails: 0, expenseSum: 0, leaveUsed: 0 };
      }
      map[d].userCount += 1;
      map[d].sentMails += emp.sentMailsCount;
      map[d].expenseSum += emp.totalExpenseAmount;
      map[d].leaveUsed += emp.usedLeaveDays;
    });

    return Object.values(map);
  }, [departments, employeeStats]);

  const handleExportCsv = () => {
    const headers = ['사번', '성명', '소속부서', '직급', '사내이메일', '발송메일수', '수신메일수', '연차사용일', '잔여연차', '경비청구총액(원)', '스토리지사용(MB)', '스토리지할당(GB)', '활동점수'];
    const rows = filteredAndSortedStats.map(e => [
      e.UserID || '',
      `"${e.Name || ''}"`,
      `"${e.Department || ''}"`,
      `"${e.Position || ''}"`,
      e.Email || '',
      e.sentMailsCount,
      e.receivedMailsCount,
      e.usedLeaveDays,
      e.remainingLeaveDays,
      e.totalExpenseAmount,
      e.storageUsageMb,
      e.storageQuotaGb,
      e.activityScore
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clouflake_employee_usage_stats_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-150">
      {/* Top Banner & Action Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-200">
              <BarChart3 className="w-5 h-5" />
            </span>
            <h2 className="text-xl font-bold text-slate-900">임직원 이용 통계 지표</h2>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
              실시간 집계 중
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            전사 임직원의 메일 송수신, 전자결재(연차/경비), 스토리지 사용 현황을 종합 분석합니다.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Monthly Period Filter Controls */}
          <div className="bg-slate-100 p-1.5 rounded-xl flex items-center gap-2 border border-slate-200 text-xs font-bold">
            <button
              onClick={() => {
                setPeriodMode('CURRENT');
                const cur = new Date();
                setSelectedYear(cur.getFullYear());
                setSelectedMonth(cur.getMonth() + 1);
              }}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                periodMode === 'CURRENT' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'text-slate-600 hover:text-slate-900 bg-white'
              }`}
            >
              당월 통계 ({now.getFullYear()}년 {now.getMonth() + 1}월)
            </button>

            <button
              onClick={() => setPeriodMode('SELECT')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                periodMode === 'SELECT' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'text-slate-600 hover:text-slate-900 bg-white'
              }`}
            >
              월별 직접 선택
            </button>

            <button
              onClick={() => setPeriodMode('ALL')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                periodMode === 'ALL' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'text-slate-600 hover:text-slate-900 bg-white'
              }`}
            >
              전체 누적
            </button>

            {periodMode === 'SELECT' && (
              <div className="flex items-center gap-1.5 pl-1 border-l border-slate-300">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="bg-white border border-slate-300 text-slate-800 rounded-lg px-2 py-1 text-xs font-bold shadow-2xs cursor-pointer focus:outline-indigo-500"
                >
                  <option value={2026}>2026년</option>
                  <option value={2025}>2025년</option>
                  <option value={2024}>2024년</option>
                </select>

                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="bg-white border border-slate-300 text-slate-800 rounded-lg px-2 py-1 text-xs font-bold shadow-2xs cursor-pointer focus:outline-indigo-500"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{m}월</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <button
            onClick={handleExportCsv}
            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer active:scale-95 ml-auto md:ml-0"
            title="통계 데이터 CSV 내보내기"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Excel / CSV 다운로드</span>
          </button>
        </div>
      </div>

      {/* Monthly Selection Active Notice Banner */}
      <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl px-4 py-2.5 flex items-center justify-between text-xs text-indigo-900 font-bold">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          <span>
            조회 기준 기간: {periodMode === 'ALL' ? '전체 누적 통계' : `${selectedYear}년 ${selectedMonth}월 (${periodMode === 'CURRENT' ? '해당월 기본 통계' : '지정월 통계'})`}
          </span>
        </div>
        <span className="text-[11px] text-indigo-600 font-mono">
          월별 메일 송수신, 연차 사용, 경비 청구 실적 집계 중
        </span>
      </div>

      {/* 1. Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Active Employees */}
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-2xs flex flex-col justify-between hover:border-indigo-300 transition-colors">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500">등록 활성 임직원</span>
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-slate-900 font-mono">{overallKpis.totalUsers}</span>
            <span className="text-xs font-medium text-slate-500 ml-1.5">명 (정상 가동)</span>
          </div>
          <div className="mt-2 text-[11px] text-emerald-600 font-bold flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            <span>100% 사내 이메일 계정 연동</span>
          </div>
        </div>

        {/* Card 2: Mail Traffic */}
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-2xs flex flex-col justify-between hover:border-indigo-300 transition-colors">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500">메일 송수신 총량</span>
            <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
              <Mail className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-slate-900 font-mono">{overallKpis.totalMails}</span>
            <span className="text-xs font-medium text-slate-500 ml-1.5">통</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 font-medium">
            발송 {overallKpis.totalSentMails}건 · SES 트래픽 정상
          </div>
        </div>

        {/* Card 3: Expense Claims */}
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-2xs flex flex-col justify-between hover:border-indigo-300 transition-colors">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500">경비 청구 누계</span>
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-slate-900 font-mono">
              ₩{overallKpis.totalExpense.toLocaleString()}
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 font-medium">
            전자결재 총 {overallKpis.totalApprovalsCount}건 처리됨
          </div>
        </div>

        {/* Card 4: Total Storage Used */}
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-2xs flex flex-col justify-between hover:border-indigo-300 transition-colors">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500">전사 스토리지 사용량</span>
            <div className="p-2 rounded-xl bg-purple-50 text-purple-600 border border-purple-100">
              <HardDrive className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-slate-900 font-mono">{overallKpis.totalStorageGb}</span>
            <span className="text-xs font-medium text-slate-500 ml-1.5">GB</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 font-medium">
            연차 소진 총 {overallKpis.totalUsedLeaves}일
          </div>
        </div>
      </div>

      {/* 2. Department Activity Overview */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-600" />
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">부서별 활동 지표 현황</h3>
          </div>
          <span className="text-[11px] text-slate-400 font-medium">총 {deptSummary.length}개 부서</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {deptSummary.map((dept, idx) => (
            <div key={idx} className="bg-white border border-slate-200 p-4 rounded-xl shadow-2xs space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-900">{dept.name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 font-bold text-slate-600">
                  {dept.userCount}명
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] pt-1 border-t border-slate-100 text-slate-600 font-mono">
                <div>
                  <span className="text-[10px] text-slate-400 block font-sans">메일 발송</span>
                  <span className="font-bold text-indigo-600">{dept.sentMails}건</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-sans">경비 청구</span>
                  <span className="font-bold text-emerald-600">₩{dept.expenseSum.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 p-4 rounded-2xl shadow-2xs">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          {/* Department Filter */}
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-medium">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={selectedDept}
              onChange={e => setSelectedDept(e.target.value)}
              className="bg-transparent focus:outline-none text-slate-800 font-bold cursor-pointer"
            >
              <option value="ALL">전체 부서 ({activeEmployees.length}명)</option>
              {departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="임직원 성명, 사내 이메일, 직급 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full text-xs bg-transparent focus:outline-none text-slate-900"
            />
          </div>
        </div>

        <span className="text-xs font-bold text-slate-500 font-mono">
          검색 결과: <span className="text-indigo-600 font-extrabold">{filteredAndSortedStats.length}</span>명
        </span>
      </div>

      {/* 4. Employee Detailed Usage Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse font-sans">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                <th className="py-3 px-4 text-slate-400 font-mono text-[11px]">#</th>
                
                <th 
                  onClick={() => handleSortChange('name')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none"
                >
                  <div className="flex items-center gap-1">
                    <span>임직원 정보</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th 
                  onClick={() => handleSortChange('activity')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none text-center"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>활동 점수</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th 
                  onClick={() => handleSortChange('mailSent')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none text-center"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>메일 송수신</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th 
                  onClick={() => handleSortChange('leaveUsed')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none"
                >
                  <div className="flex items-center gap-1">
                    <span>연차 사용 현황</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th 
                  onClick={() => handleSortChange('expenseSum')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none text-right"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>경비 청구 누계</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th 
                  onClick={() => handleSortChange('storage')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors font-extrabold text-slate-900 select-none"
                >
                  <div className="flex items-center gap-1">
                    <span>스토리지 사용량</span>
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </th>

                <th className="py-3 px-4 text-slate-600 font-bold text-center">
                  최근 활동
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAndSortedStats.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400 text-xs font-medium">
                    일치하는 임직원 통계 정보가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredAndSortedStats.map((emp, idx) => (
                  <tr key={emp.UserID} className="hover:bg-indigo-50/30 transition-colors">
                    {/* Index */}
                    <td className="py-3.5 px-4 font-mono text-[11px] text-slate-400">
                      {idx + 1}
                    </td>

                    {/* Employee Profile */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                          {emp.Name ? emp.Name.slice(0, 1) : 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-slate-900 truncate">{emp.Name}</span>
                            <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-600 font-medium">
                              {emp.Position || '사원'}
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 block font-mono truncate">{emp.Email}</span>
                          <span className="text-[10px] text-indigo-600 font-semibold">{emp.Department}</span>
                        </div>
                      </div>
                    </td>

                    {/* Activity Score */}
                    <td className="py-3.5 px-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                        <Sparkles className="w-3 h-3 text-amber-500" />
                        {emp.activityScore}점
                      </span>
                    </td>

                    {/* Mail Stats */}
                    <td className="py-3.5 px-4 text-center font-mono">
                      <div className="inline-flex flex-col items-center">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-indigo-600 font-bold">발송 {emp.sentMailsCount}</span>
                          <span className="text-slate-300">/</span>
                          <span className="text-slate-700 font-medium">수신 {emp.receivedMailsCount}</span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-sans">총 {emp.totalMailsCount}건</span>
                      </div>
                    </td>

                    {/* Leave Usage */}
                    <td className="py-3.5 px-4">
                      <div className="space-y-1 max-w-[140px]">
                        <div className="flex justify-between text-[11px] font-mono">
                          <span className="text-slate-700 font-bold">사용 {emp.usedLeaveDays}일</span>
                          <span className="text-slate-400">잔여 {emp.remainingLeaveDays}일</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className="bg-indigo-600 h-1.5 rounded-full"
                            style={{ width: `${emp.leaveUsagePercent}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Expense Sum */}
                    <td className="py-3.5 px-4 text-right font-mono">
                      <span className="font-bold text-slate-900 text-xs">
                        ₩{emp.totalExpenseAmount.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-slate-400 block font-sans">
                        {emp.expenseApprovalsCount}건 청구
                      </span>
                    </td>

                    {/* Storage Usage */}
                    <td className="py-3.5 px-4">
                      <div className="space-y-1 max-w-[130px]">
                        <div className="flex justify-between text-[11px] font-mono">
                          <span className="text-slate-800 font-bold">{emp.storageUsageMb} MB</span>
                          <span className="text-slate-400">/ {emp.storageQuotaGb} GB</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className={`h-1.5 rounded-full ${
                              emp.storageUsagePercent > 80 ? 'bg-rose-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.max(emp.storageUsagePercent, 4)}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Latest Activity */}
                    <td className="py-3.5 px-4 text-center font-mono text-[11px] text-slate-500">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{emp.latestActivity ? emp.latestActivity.slice(0, 10) : '-'}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;
